import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ContainerWrapper,
  FooterWrapper,
  Navbar,
  Anchor,
  MediaList,
  MediaLink,
  LeftWrapper,
  RightWrapper,
  CollapsWrapper,
} from './style';
import useResize from '@/hooks/useResize';
import useGetFeatures from '@/hooks/useGetFeatures';
import useCurrentLang from '@/hooks/useCurrentLang';
import { Box } from '@/layout';
import { Mail, Phone, Pin } from '@/components/Icon';
import { theme } from '@/theme/Theme';
import { sify } from 'chinese-conv/dist';
import { Collapse, List, ListItem, ListItemText } from '@mui/material';
import { Text } from '@/components/UI/Typography/Text';
import { useGetPolicyTerms } from '@/hooks/useGetPolicyTerms';
import {
  applyPolicyDocumentsToFooterLinks,
  isExternalFooterUrl,
  type FooterLinkGroup,
} from './footerLinks';


const Footer = () => {
  return <DesktopFooter />;
};

const DesktopFooter = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const currentLang = useCurrentLang();
  const { isMobile, isTablet } = useResize();
  const footerLinks = t('pages.footer', { returnObjects: true }) as FooterLinkGroup[];
  const features = useGetFeatures();
  const navigate = useNavigate();
  const [showFooter, setShowFooter] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { policyUrl, termsUrl } = useGetPolicyTerms();
  const [data, setData] = useState<FooterLinkGroup[]>([]);

  const socialMedia = [
    // {
    //   icon: '/images/socialMedia/facebook.svg',
    //   link: 'https://www.facebook.com/eBRAM.ODR',
    // },
    {
      icon: '/images/socialMedia/linkedin.svg',
      link: 'https://www.linkedin.com/company/ebram-odr/',
    },
    {
      icon: '/images/socialMedia/WhatsAPP.svg',
      link: 'https://wa.me/85266981322',
    },
  ];

  const initData = () => {
    if (features.length === 0) return;
    // 將features陣列的資料按照weight排序, 0為最優先, 若weight相同則按照created_at排序
    const sortedFeatures = [...features].sort((a, b) => {
      if (a.weight === b.weight) {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return b.weight - a.weight;
    });

    // 將排序後的features透過map轉換成和{name: string; url: string}[]格式
    const portalFeaturesLinks = sortedFeatures.map((item) => {
      if (!item.cus_title_tc) {
        item.cus_title_tc = '';
      }
      return (
        {
          key: 'Portal Features',
          name: currentLang === 'en' ? item.cus_title : currentLang === 'tc' ? item.cus_title_tc : sify(item.cus_title_tc),
          url: `/features/${item.cus_title}`,
        }
      )
    });

    const localizedFeatures =
      currentLang === 'en'
        ? portalFeaturesLinks.map((item) => {
            if (!item.name) return item;

            const name = item.name
              .replace(/e-signature/gi, 'e-Signature')
              .replace(/e-Signature/g, '__ES__')
              .replace(/-/g, ' ')
              .replace(/__ES__/g, 'e-Signature')
              .split(' ')
              .map((word) => (word === 'e-Signature' ? word : word.charAt(0).toUpperCase() + word.slice(1)))
              .join(' ');

            return { ...item, name };
          })
        : portalFeaturesLinks;

    const newFooterLinks = applyPolicyDocumentsToFooterLinks(
      footerLinks.map((item) =>
        item.key === 'Portal Features' ? { ...item, link: localizedFeatures } : item
      ),
      { policyUrl, termsUrl }
    );

    // 存入data
    setData(newFooterLinks);
    setShowFooter(true);
  }

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    setShowFooter(false);
    initData();
  }, [features, t, currentLang, policyUrl, termsUrl]);

  const handleClick = (idx: number) => {
    setOpenIndex(prev => (prev === idx ? null : idx)); // 再點一次可收起
  };

  const redirHandler = (url: string) => {
    navigate(url);
  };
  return (
    <ContainerWrapper>
      {
        isTablet && (
          <FooterWrapper isTablet={isTablet} isMobile={isMobile}>

            <LeftWrapper isTablet={isTablet} isMobile={isMobile}>
              <div className="title">
                {t('pages.index.Contact.title')}
              </div>
              <div className="flex">
                <div className="infoBox">
                  <Box width={'24px'}>
                    <Pin.PinSVG />
                  </Box>
                  <div>{t('pages.index.footer.address')}</div>
                </div>
                <div className="infoBox">
                  <Mail.MailSVG />
                  <a href={`mailto:${t('pages.index.footer.email')}`}>{t('pages.index.footer.email')}</a>
                </div>
                <div className="infoBox">
                  <Phone.PhoneSVG />
                  <div>{t('pages.index.footer.phone')}</div>
                </div>
                <MediaList isTablet={isTablet} isMobile={isMobile}>
                  {socialMedia.map((item, index) => (
                    <MediaLink key={item.link + index} target="_blank" href={item.link}>
                      <img width={'100%'} src={item.icon} alt="icon" />
                    </MediaLink>
                  ))}
                </MediaList>
              </div>
            </LeftWrapper>
            <RightWrapper isTablet={isTablet} isMobile={isMobile}>
              {
                showFooter && data.map((linkgroup, index) => (
                  <Box onClick={() => handleClick(index)} key={linkgroup.title + index} color={theme.colors.light} style={{ cursor: 'pointer', borderTop: '1px solid #fff', borderBottom: index === data.length - 1 ? '1px solid #fff' : '' }}>
                    <ListItem>
                      <Box width={'100%'}>
                        <Text>{linkgroup.title}</Text>
                      </Box>
                      <img
                        style={openIndex === index ? { transform: 'rotateZ(90deg)', transition: '.5s' } : { transform: 'rotateZ(-90deg)', transition: '.5s' }}
                        width="32"
                        src={`/images/navIcons/ArrowRight.svg`}
                        alt=""
                      />
                    </ListItem>
                    <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                      <CollapsWrapper>
                        <List component="div" disablePadding>
                          {linkgroup.link.map((item) => (
                            isExternalFooterUrl(item.url) ? (
                              <ListItem
                                button
                                component="a"
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                key={item.name}
                              >
                                <ListItemText primary={item.name} />
                              </ListItem>
                            ) : (
                              <ListItem button key={item.name} onClick={() => redirHandler(item.url)}>
                                <ListItemText primary={item.name} />
                              </ListItem>
                            )
                          ))}
                        </List>
                      </CollapsWrapper>
                    </Collapse>
                  </Box>
                ))
              }
            </RightWrapper>
          </FooterWrapper>
        )
      }

      {
        !isTablet && (
          <FooterWrapper isTablet={isTablet} isMobile={isMobile}>
            <LeftWrapper isTablet={isTablet} isMobile={isMobile}>
              <div className="title">
                {t('pages.index.Contact.title')}
              </div>
              <div className="infoBox">
                <Box width={'24px'}>
                  <Pin.PinSVG />
                </Box>
                <div>{t('pages.index.footer.address')}</div>
              </div>
              <div className="infoBox">
                <Mail.MailSVG />
                <a href={`mailto:${t('pages.index.footer.email')}`}>{t('pages.index.footer.email')}</a>
              </div>
              <div className="infoBox">
                <Phone.PhoneSVG />
                <div>{t('pages.index.footer.phone')}</div>
              </div>
              <MediaList isTablet={isTablet} isMobile={isMobile}>
                {socialMedia.map((item, index) => (
                  <MediaLink key={item.link + index} target="_blank" href={item.link}>
                    <img width={'100%'} src={item.icon} alt="icon" />
                  </MediaLink>
                ))}
              </MediaList>
            </LeftWrapper>
            <RightWrapper isTablet={isTablet} isMobile={isMobile}>
              {
                showFooter && (
                  <Navbar isTablet={isTablet} isMobile={isMobile}>
                    {data.map((footer, index) => (
                      <div key={footer.title + index} style={data.length - 2 <= index ? { flexShrink: 0 } : {}}>
                        <div className="title">{footer.title}</div>
                        {footer.link.map((target, index) => {
                          const isExternal = isExternalFooterUrl(target.url);
                          return isExternal ? (
                            <a key={target.name + index} href={target.url} target="_blank" rel="noopener noreferrer">
                              <Anchor>{target.name}</Anchor>
                            </a>
                          ) : (
                            <Link key={target.name + index} to={`/${currentLang}${target.url}`} onClick={toTop}>
                              <Anchor>{target.name}</Anchor>
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                  </Navbar>
                )
              }
            </RightWrapper>
          </FooterWrapper>
        )
      }
      <Box padding={12} textAlign={'center'} backgroundColor={theme.colors.primary500} color={theme.colors.light}>{t('pages.index.footer.copyright')}</Box>
    </ContainerWrapper >
  );
};

export default Footer;
